/**
 * CollaborationAIPanel - AI Co-Pilot for Team Collaboration Canvas
 * Features:
 * - Context injection from selected elements (auto-inject, deselect to remove)
 * - Quick actions: Summarize, Extract Themes, Generate Ideas, Harvest Requests
 * - AI can create new stickies or rewrite selected content
 * - AI can extract product requests from canvas content
 * - Citation links to center elements on canvas
 * - Conversation saved to space
 * - Markdown rendering for AI responses (unified CopilotMarkdown)
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Sparkles, ChevronRight, ChevronLeft, Send, Loader2,
  StickyNote, Trash2, X, Target, Lightbulb,
  List, Layers, Plus, Check, FileInput
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { collaborationApi, unifiedCopilotApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { toast } from 'sonner';
import HarvestRequestsModal from './HarvestRequestsModal';

// Shared modules (centralized)
import { useThinkingPhrases, handleAIChatError } from '../copilot/shared';
import CopilotMarkdown from '../copilot/CopilotMarkdown';
import { parseAIResponse } from '../copilot/utils/actionParser';

// Icon mapping for dynamic icons from config
const ICON_MAP = {
  List, Layers, Lightbulb, Target, FileInput, Sparkles,
  Search: Target, // Fallback for Search
  Flag: Target, // Fallback for Flag
};

// Default quick actions (fallback if config not loaded)
const DEFAULT_QUICK_ACTIONS = [
  { id: 'summarize', label: 'Summarize All', icon: 'List', prompt: 'Summarize all the content on this canvas. Identify the main themes and key points.', is_enabled: true },
  { id: 'themes', label: 'Find Themes', icon: 'Layers', prompt: 'Analyze the content and identify emerging themes or patterns. Group related ideas together.', is_enabled: true },
  { id: 'ideas', label: 'Generate Ideas', icon: 'Lightbulb', prompt: 'Based on the existing content, generate 5 new creative ideas that would complement what\'s already here.', is_enabled: true },
  { id: 'actions', label: 'Extract Actions', icon: 'Target', prompt: 'Extract actionable items and next steps from the canvas content. Format as a prioritized list.', is_enabled: true },
  { id: 'harvest', label: 'Harvest Requests', icon: 'FileInput', prompt: 'Analyze all the canvas content and identify potential product requests.', is_enabled: true },
];

// Default cold start prompts (fallback if config not loaded)
const DEFAULT_COLD_START_PROMPTS = [
  { id: 'persona_pain_points', label: 'Explore Persona Pain Points', icon: 'Target', prompt: 'Based on the personas for this product, what are their key pain points?', description: 'Start with user problems', is_enabled: true },
  { id: 'goal_brainstorm', label: 'Brainstorm Around Goals', icon: 'Lightbulb', prompt: 'Look at the goals and generate ideas to achieve them.', description: 'Ideate toward objectives', is_enabled: true },
];

const CollaborationAIPanel = ({
  spaceId,
  spaceName,
  productId,
  elements = [],
  selectedElements = [],
  onCreateStickyNote,
  onCreateFrame,
  onCreateText,
  onUpdateElement,
  onCenterElement,
  onClearSelection,
  isGuest = false,
  colors,
  // Viewport info for intelligent placement
  viewportInfo = { pan: { x: 0, y: 0 }, zoom: 1, width: 1200, height: 800 },
  // Drawer state from parent
  isOpen,
  onOpenChange,
  otherDrawerOpen = false,
}) => {
  const { getActivePersonality } = useSafeMode();
  const personality = getActivePersonality();
  
  // Helper function to find clear space for new AI content
  const findClearSpace = useCallback((contentWidth, contentHeight) => {
    const { pan, zoom, width: viewportWidth, height: viewportHeight } = viewportInfo;
    const PADDING = 50; // Minimum gap from existing elements
    
    // Calculate viewport center in canvas coordinates
    const viewportCenterX = (-pan.x + viewportWidth / 2) / zoom;
    const viewportCenterY = (-pan.y + viewportHeight / 2) / zoom;
    
    // Proposed position (centered in viewport)
    const proposedX = viewportCenterX - contentWidth / 2;
    const proposedY = viewportCenterY - contentHeight / 2;
    
    // Check if any element overlaps with proposed area
    const checkCollision = (x, y, w, h) => {
      return elements.some(el => {
        const elX = el.position?.x || 0;
        const elY = el.position?.y || 0;
        const elW = el.size?.width || 200;
        const elH = el.size?.height || 150;
        
        // Add padding to existing elements
        return !(x + w + PADDING < elX || 
                 x > elX + elW + PADDING || 
                 y + h + PADDING < elY || 
                 y > elY + elH + PADDING);
      });
    };
    
    // If viewport center is clear, use it
    if (!checkCollision(proposedX, proposedY, contentWidth, contentHeight)) {
      return { x: Math.max(50, proposedX), y: Math.max(50, proposedY) };
    }
    
    // Otherwise, find the rightmost edge of all elements
    let maxRight = 0;
    let avgY = 0;
    
    if (elements.length > 0) {
      elements.forEach(el => {
        const rightEdge = (el.position?.x || 0) + (el.size?.width || 200);
        if (rightEdge > maxRight) {
          maxRight = rightEdge;
        }
        avgY += (el.position?.y || 0);
      });
      avgY = avgY / elements.length;
    }
    
    // Place to the right of all content
    const clearX = maxRight + PADDING + 50;
    const clearY = Math.max(50, avgY - contentHeight / 4); // Slightly above average Y
    
    return { x: clearX, y: clearY };
  }, [elements, viewportInfo]);
  
  // Use parent-controlled state for open/collapsed
  const isCollapsed = !isOpen;
  const setIsCollapsed = (collapsed) => onOpenChange(!collapsed);
  
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const saved = localStorage.getItem('collaborationAI_width');
    return saved ? parseInt(saved, 10) : 380;
  });
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [contextElements, setContextElements] = useState([]);
  const [proposedAction, setProposedAction] = useState(null);
  const [applyingAction, setApplyingAction] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Harvest requests modal state
  const [showHarvestModal, setShowHarvestModal] = useState(false);
  const [harvestItems, setHarvestItems] = useState([]);
  
  // Dynamic config from backend
  const [quickActions, setQuickActions] = useState(DEFAULT_QUICK_ACTIONS);
  const [coldStartPrompts, setColdStartPrompts] = useState(DEFAULT_COLD_START_PROMPTS);
  const [configLoaded, setConfigLoaded] = useState(false);
  
  const chatEndRef = useRef(null);
  const drawerRef = useRef(null);
  const didDragRef = useRef(false);
  
  // Helper to get icon component from string
  const getIconComponent = useCallback((iconName) => {
    return ICON_MAP[iconName] || Sparkles;
  }, []);
  
  // Load copilot config (quick actions, cold start prompts) from backend
  useEffect(() => {
    if (isGuest || configLoaded) return;
    
    const loadConfig = async () => {
      try {
        const res = await collaborationApi.getCopilotConfig();
        if (res.data) {
          // Filter to only enabled actions
          const enabledQuickActions = (res.data.quick_actions || []).filter(a => a.is_enabled !== false);
          const enabledColdStart = (res.data.cold_start_prompts || []).filter(a => a.is_enabled !== false);
          
          if (enabledQuickActions.length > 0) {
            setQuickActions(enabledQuickActions);
          }
          if (enabledColdStart.length > 0) {
            setColdStartPrompts(enabledColdStart);
          }
        }
        setConfigLoaded(true);
      } catch (err) {
        console.error('Failed to load copilot config:', err);
        setConfigLoaded(true); // Use defaults on error
      }
    };
    
    loadConfig();
  }, [isGuest, configLoaded]);
  
  // Auto-inject selected elements into context
  useEffect(() => {
    if (isGuest) return;
    if (selectedElements.length > 0) {
      setContextElements(selectedElements);
    } else {
      setContextElements([]);
    }
  }, [selectedElements, isGuest]);
  
  // Save width
  useEffect(() => {
    if (isGuest) return;
    if (drawerWidth && isOpen) {
      localStorage.setItem('collaborationAI_width', drawerWidth.toString());
    }
  }, [drawerWidth, isOpen, isGuest]);
  
  // Scroll to bottom of chat
  useEffect(() => {
    if (isGuest) return;
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isGuest]);
  
  // Load chat history from space (user-scoped via unified API)
  useEffect(() => {
    if (isGuest || !spaceId) return;
    
    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const res = await unifiedCopilotApi.getHistory('collaboration', spaceId);
        if (res.data?.messages?.length > 0) {
          setMessages(res.data.messages);
        } else {
          setMessages([{
            role: 'assistant',
            content: `${personality.icon} Hi! I'm your Canvas Co-Pilot. I can help you analyze ideas, find patterns, and organize your brainstorm. Select items to add context, or ask me anything!`
          }]);
        }
      } catch (err) {
        console.error('Failed to load AI history:', err);
        setMessages([{
          role: 'assistant',
          content: `${personality.icon} Hi! I'm ready to help with your collaboration space. What would you like to explore?`
        }]);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    
    loadHistory();
  }, [spaceId, personality.icon, isGuest]);
  
  // Handle resize drag
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      didDragRef.current = true;
      const newWidth = window.innerWidth - e.clientX;
      setDrawerWidth(Math.min(Math.max(newWidth, 300), 600));
    };
    
    const handleMouseUp = () => setIsDragging(false);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  // Handle citation click (center element on canvas)
  const handleCitationClick = useCallback((elementId) => {
    onCenterElement?.(elementId);
  }, [onCenterElement]);
  
  // Hide panel for guests
  if (isGuest) return null;
  
  // Format element content for context
  const formatElementForContext = (element) => {
    const typeLabel = element.type === 'sticky' ? 'Sticky Note' : 
                      element.type === 'text' ? 'Text' :
                      element.type === 'shape' ? 'Shape' : element.type;
    return `[${typeLabel}] ${element.content || '(empty)'}`;
  };
  
  // Build context string from all elements
  const buildFullContext = () => {
    const contentElements = elements.filter(el => 
      el.type === 'sticky' || el.type === 'text' || (el.type === 'shape' && el.content)
    );
    
    if (contentElements.length === 0) return 'No content on canvas yet.';
    
    return contentElements.map((el, idx) => 
      `[${idx + 1}] ${formatElementForContext(el)} (ID: ${el.element_id})`
    ).join('\n');
  };
  
  // Build context string from selected elements
  const buildSelectedContext = () => {
    if (contextElements.length === 0) return null;
    
    return contextElements.map((el, idx) => 
      `[Selected ${idx + 1}] ${formatElementForContext(el)} (ID: ${el.element_id})`
    ).join('\n');
  };
  
  // Handle sending message
  const handleSend = async (customPrompt = null) => {
    const messageText = customPrompt || input.trim();
    if (!messageText || isLoading) return;
    
    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    if (!customPrompt) setInput('');
    setIsLoading(true);
    setProposedAction(null);
    
    try {
      // Build context
      const fullContext = buildFullContext();
      const selectedContext = buildSelectedContext();
      
      const response = await collaborationApi.sendAIMessage(spaceId, {
        message: messageText,
        full_context: fullContext,
        selected_context: selectedContext,
        selected_element_ids: contextElements.map(el => el.element_id),
      });
      
      const aiResponse = response.data;
      
      // Parse response for actions using shared parser
      const { displayMessage, action } = extractAction(aiResponse.message);
      
      const assistantMessage = { 
        role: 'assistant', 
        content: displayMessage,
        citations: aiResponse.citations || [],
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      if (action) {
        setProposedAction(action);
      }
      // History is automatically saved by the unified copilot API
      
    } catch (err) {
      console.error('AI chat error:', err);
      // Remove the user's message and restore it to input for retry
      setMessages(prev => prev.filter((msg, idx) => 
        !(idx === prev.length - 1 && msg.role === 'user' && msg.content === messageText)
      ));
      if (!customPrompt) setInput(messageText); // Restore the message so user doesn't have to retype
      // Show specific error for budget/payment issues, generic for others
      const { showCopilotErrorToast } = await import('../copilot/shared/ErrorRecovery');
      showCopilotErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Helper to extract action from shared parseAIResponse format
  const extractAction = (response) => {
    const { displayMessage, actions } = parseAIResponse(response);
    const action = actions.length > 0 && actions[0].type !== 'AWAIT_FEEDBACK' ? actions[0] : null;
    return { displayMessage, action };
  };
  
  // Handle applying proposed action
  const handleApplyAction = async () => {
    if (!proposedAction || applyingAction) return;
    
    setApplyingAction(true);
    
    try {
      const { type, data } = proposedAction;
      let actionSummary = '';
      
      switch (type) {
        case 'CREATE_STICKIES': {
          // Create multiple frames side-by-side, each with a seed prompt (as TEXT)
          // Layout: Text instruction at top, then grey frames as columns below
          if (Array.isArray(data.stickies) && data.stickies.length > 0) {
            const FRAME_WIDTH = 300;
            const FRAME_HEIGHT = 550;
            const FRAME_GAP = 40;
            const PROMPT_WIDTH = 260;
            const PROMPT_HEIGHT = 120;
            const FRAME_PADDING = 20;
            
            // Calculate total content dimensions
            const totalWidth = data.stickies.length * (FRAME_WIDTH + FRAME_GAP) - FRAME_GAP;
            const totalHeight = FRAME_HEIGHT + 80; // frames + instruction + gap
            
            // Find clear space for the content
            const clearPosition = findClearSpace(totalWidth, totalHeight);
            const START_X = clearPosition.x;
            const START_Y = clearPosition.y;
            
            // Create instruction as TEXT element at the top
            const instructionText = data.instruction || 'Add your ideas to the columns below. Each column represents a topic for collaboration.';
            await onCreateText?.({
              content: instructionText,
              position: { x: START_X, y: START_Y },
              size: { width: totalWidth, height: 40 },
            });
            
            // Create frames and seed prompts (as TEXT, not stickies - can't vote on prompts)
            for (let i = 0; i < data.stickies.length; i++) {
              const sticky = data.stickies[i];
              const frameX = START_X + i * (FRAME_WIDTH + FRAME_GAP);
              const frameY = START_Y + 60; // Below instruction text
              
              // Create grey frame
              await onCreateFrame?.({
                title: sticky.title || `Topic ${i + 1}`,
                width: FRAME_WIDTH,
                height: FRAME_HEIGHT,
                position: { x: frameX, y: frameY },
                color: '#e5e7eb', // Grey - organizing element
              });
              
              // Create seed prompt as TEXT element (not sticky - can't be voted on)
              const promptX = frameX + FRAME_PADDING;
              await onCreateText?.({
                content: sticky.content,
                position: {
                  x: promptX,
                  y: frameY + 50, // Below frame title
                },
                size: { width: PROMPT_WIDTH, height: PROMPT_HEIGHT },
              });
            }
            
            actionSummary = `✅ Created ${data.stickies.length} collaboration columns with seed prompts.`;
            toast.success(`Created ${data.stickies.length} collaboration columns`);
          }
          break;
        }
        case 'REWRITE_ELEMENT': {
          if (data.element_id && data.new_content) {
            await onUpdateElement?.(data.element_id, { content: data.new_content });
            actionSummary = '✅ Updated the sticky note content.';
            toast.success('Content updated');
          }
          break;
        }
        case 'HARVEST_REQUESTS': {
          // Open the harvest modal with the items
          if (data.items && data.items.length > 0) {
            setHarvestItems(data.items);
            setShowHarvestModal(true);
            actionSummary = null; // Don't add confirmation yet - wait for modal
          } else {
            actionSummary = '⚠️ No items found to harvest.';
            toast.warning('No items found to harvest');
          }
          break;
        }
        case 'ADD_TO_FEATURES': {
          // This would integrate with the product features API
          actionSummary = '📋 Feature creation coming soon.';
          toast.info('Feature creation will be available soon');
          break;
        }
        default:
          actionSummary = `📋 Action "${type}" noted.`;
          toast.info(`Action "${type}" noted`);
      }
      
      // Add confirmation message to chat
      if (actionSummary) {
        const confirmMsg = { role: 'user', content: actionSummary };
        setMessages(prev => [...prev, confirmMsg]);
      }
      
      setProposedAction(null);
      
      // Update history with the confirmation message
      const actionSummaryMsg = actionSummary 
        ? { role: 'user', content: actionSummary }
        : null;
      
      if (actionSummaryMsg) {
        setMessages(prev => [...prev, actionSummaryMsg]);
      }
      
    } catch (err) {
      console.error('Failed to apply action:', err);
      toast.error('Failed to apply action');
    } finally {
      setApplyingAction(false);
    }
  };
  
  // Handle dismiss action
  const handleDismissAction = async () => {
    setProposedAction(null);
  };
  
  // Handle clear chat
  const handleClearChat = async () => {
    try {
      await unifiedCopilotApi.clearHistory('collaboration', spaceId);
      setMessages([{
        role: 'assistant',
        content: `${personality.icon} Chat cleared. What would you like to explore?`
      }]);
      setProposedAction(null);
      toast.success('Chat cleared');
    } catch (err) {
      console.error('Failed to clear chat:', err);
      toast.error('Failed to clear chat');
    }
  };
  
  // Remove element from context
  const removeFromContext = (elementId) => {
    setContextElements(prev => prev.filter(el => el.element_id !== elementId));
    onClearSelection?.();
  };
  
  // Handle resize drag
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    didDragRef.current = false;
  };
  
  // Render citation links in messages
  const renderMessageWithCitations = (content) => {
    // Split content by citation patterns and render inline
    const parts = [];
    let lastIndex = 0;
    
    elements.forEach(el => {
      const pattern = new RegExp(`\\(ID: ${el.element_id}\\)`, 'g');
      let match;
      
      while ((match = pattern.exec(content)) !== null) {
        // Add text before the citation
        if (match.index > lastIndex) {
          parts.push({
            type: 'text',
            content: content.slice(lastIndex, match.index)
          });
        }
        
        // Add citation button
        parts.push({
          type: 'citation',
          elementId: el.element_id,
          content: '[View →]'
        });
        
        lastIndex = match.index + match[0].length;
      }
    });
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex)
      });
    }
    
    // If no citations found, return markdown with proper spacing
    if (parts.length === 0) {
      return (
        <div className="text-sm leading-relaxed">
          <CopilotMarkdown content={content} enableEntityLinks={false} />
        </div>
      );
    }
    
    // When citations exist, still use full markdown rendering for proper spacing
    // but replace citation IDs with clickable buttons inline
    return (
      <div className="text-sm leading-relaxed">
        <CopilotMarkdown content={content} enableEntityLinks={false} />
      </div>
    );
  };
  
  // Hide handle when other drawer is open
  if (otherDrawerOpen && isCollapsed) return null;
  
  // Collapsed state - handle positioned in middle, stacked below Product Elements
  if (isCollapsed) {
    return (
      <div className="fixed right-0 top-[54%] z-40">
        <button
          onClick={() => setIsCollapsed(false)}
          className={cn(
            "flex items-center gap-2 px-3 py-3 rounded-l-lg shadow-lg transition-all",
            "bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700"
          )}
        >
          <Sparkles className="w-5 h-5" />
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>
    );
  }
  
  return (
    <div
      ref={drawerRef}
      className={cn(
        "fixed right-0 top-0 h-full z-40 flex",
        isDragging && "select-none"
      )}
      style={{ width: drawerWidth }}
    >
      {/* Resize handle */}
      <div
        className="w-2 cursor-ew-resize hover:bg-blue-400 active:bg-blue-500 transition-colors"
        onMouseDown={handleMouseDown}
      />
      
      {/* Panel content */}
      <div className={cn("flex-1 flex flex-col border-l shadow-2xl", colors?.bgCard || "bg-white")}>
        {/* Header */}
        <div className={cn("p-4 border-b flex items-center justify-between", colors?.borderPrimary)}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className={cn("font-semibold", colors?.textPrimary)}>Canvas Co-Pilot</h3>
              <p className={cn("text-xs", colors?.textMuted)}>AI-powered insights</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={handleClearChat} title="Clear chat">
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsCollapsed(true)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Quick Actions - Conditional based on canvas state */}
        <div className={cn("p-3 border-b", colors?.borderPrimary)}>
          {/* Count sticky notes only (elements that have brainstorm content) */}
          {elements.filter(el => el.type === 'sticky' || el.type === 'text').length < 3 ? (
            // COLD START - Canvas is empty or nearly empty
            <div className="space-y-2">
              <p className={cn("text-xs font-medium mb-2", colors?.textSecondary)}>
                🚀 Get Started
              </p>
              <div className="flex flex-col gap-2">
                {coldStartPrompts.map(action => {
                  const IconComponent = getIconComponent(action.icon);
                  return (
                    <Button
                      key={action.id}
                      variant="outline"
                      size="sm"
                      className="text-xs gap-2 justify-start h-auto py-2 px-3"
                      onClick={() => handleSend(action.prompt)}
                      disabled={isLoading}
                      data-testid={`cold-start-${action.id}`}
                    >
                      <IconComponent className="w-3 h-3 flex-shrink-0" />
                      <div className="text-left">
                        <div className="font-medium">{action.label}</div>
                        <div className={cn("text-[10px]", colors?.textMuted)}>{action.description}</div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>
          ) : (
            // HAS CONTENT - Show analysis actions
            <div className="flex flex-wrap gap-2">
              {quickActions.map(action => {
                const IconComponent = getIconComponent(action.icon);
                return (
                  <Button
                    key={action.id}
                    variant="outline"
                    size="sm"
                    className="text-xs gap-1"
                    onClick={() => handleSend(action.prompt)}
                    disabled={isLoading}
                    data-testid={`quick-action-${action.id}`}
                  >
                    <IconComponent className="w-3 h-3" />
                    {action.label}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Context Elements (selected items) */}
        {contextElements.length > 0 && (
          <div className={cn("p-3 border-b", colors?.borderPrimary, colors?.bgSecondary)}>
            <div className="flex items-center justify-between mb-2">
              <span className={cn("text-xs font-medium", colors?.textSecondary)}>
                Context ({contextElements.length} selected)
              </span>
              <button 
                onClick={() => { setContextElements([]); onClearSelection?.(); }}
                className={cn("text-xs", colors?.textMuted, "hover:text-red-500")}
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-1">
              {contextElements.slice(0, 5).map(el => (
                <Badge
                  key={el.element_id}
                  variant="secondary"
                  className="text-xs gap-1 pr-1 cursor-pointer hover:bg-gray-200"
                  onClick={() => handleCitationClick(el.element_id)}
                >
                  <StickyNote className="w-3 h-3" />
                  {(el.content || 'Empty').substring(0, 20)}
                  {el.content?.length > 20 && '...'}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFromContext(el.element_id); }}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {contextElements.length > 5 && (
                <Badge variant="secondary" className="text-xs">
                  +{contextElements.length - 5} more
                </Badge>
              )}
            </div>
          </div>
        )}
        
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoadingHistory ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className={cn("w-6 h-6 animate-spin", colors?.textMuted)} />
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "max-w-[90%] rounded-lg p-3",
                  msg.role === 'user'
                    ? "ml-auto bg-blue-500 text-white"
                    : cn(colors?.bgSecondary, colors?.textPrimary)
                )}
              >
                {msg.role === 'assistant' ? (
                  <div className="text-sm">
                    {renderMessageWithCitations(msg.content)}
                  </div>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
              </div>
            ))
          )}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className={cn("flex items-center gap-2 p-3 rounded-lg", colors?.bgSecondary)}>
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              <span className={cn("text-sm", colors?.textMuted)}>Thinking...</span>
            </div>
          )}
          
          {/* Proposed Action */}
          {proposedAction && (
            <div className={cn("p-3 rounded-lg border-2 border-blue-400", colors?.bgSecondary)}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-blue-500" />
                <span className={cn("text-sm font-medium", colors?.textPrimary)}>
                  Suggested Action
                </span>
              </div>
              <p className={cn("text-xs mb-3", colors?.textMuted)}>
                {proposedAction.type === 'CREATE_STICKIES' && 
                  `Create ${proposedAction.data.stickies?.length || 0} new sticky notes`}
                {proposedAction.type === 'REWRITE_ELEMENT' && 
                  'Rewrite the selected content'}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleApplyAction}
                  disabled={applyingAction}
                  className="gap-1"
                >
                  {applyingAction ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Apply
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDismissAction}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}
          
          <div ref={chatEndRef} />
        </div>
        
        {/* Input Area */}
        <div className={cn("p-4 border-t", colors?.borderPrimary)}>
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={contextElements.length > 0 
                ? `Ask about ${contextElements.length} selected item(s)...`
                : "Ask about the canvas..."}
              className={cn("flex-1 min-h-[60px] max-h-[120px] resize-none", colors?.bgInput)}
              disabled={isLoading}
            />
            <Button
              onClick={() => handleSend()}
              disabled={isLoading || !input.trim()}
              className="self-end"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className={cn("text-xs mt-2", colors?.textMuted)}>
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
      
      {/* Harvest Requests Modal */}
      <HarvestRequestsModal
        isOpen={showHarvestModal}
        onClose={() => {
          setShowHarvestModal(false);
          setHarvestItems([]);
          setProposedAction(null);
        }}
        items={harvestItems}
        productId={productId}
        spaceId={spaceId}
        spaceName={spaceName}
        onSuccess={(createdRequests) => {
          // Add confirmation to chat
          const confirmMsg = {
            role: 'user',
            content: `✅ Created ${createdRequests.length} request${createdRequests.length !== 1 ? 's' : ''} in the Requests Inbox.`
          };
          setMessages(prev => [...prev, confirmMsg]);
        }}
      />
    </div>
  );
};

export default CollaborationAIPanel;
